import { defineEffect } from '../../../core/effect';
import { tween } from '../../../core/effect/motion';
import { add, card, fade, place, zoneBox } from '../fx/common';

/**
 * **発動後**（発動し終えた魔法・罠が墓地へ。Issue #562。指示書 `../../direction/move.md` の「発動後」）。
 * 魔法・罠ゾーンのカードが**盤のカードのまま**だんだん透明になって消えるだけ。文字・光・動き・SE は無い
 * （1 試合 15 回ほど出るので短く控えめに）。
 *
 * ```
 * 0     盤のカード（移動前の盤面の表の絵・向き）がそのまま居る
 * 0〜400 だんだん透明になる（終わりほど早く抜ける）
 * 400   消え終わり: 盤面がイベントの後へ進み、墓地の一番上がこのカードになる
 * ```
 *
 * **盤面は消え終わったとき（尺の終わり）に進める**（口 `board`。Issue #566）。それまでは盤のカードを
 * `tweaks` で借りて素材の濃さを下げていく（演出の板で描き直さない）。
 * uid が分からない（盤のカードを借りられない）ときだけ、盤面を帯の頭で進め、同じ絵を演出の板で描いて消す。
 */
export const D = 400;

/** 時刻 t の濃さ（1 → 0） */
export const alphaAt = (t) => (t >= D ? 0 : 1 - tween(t, 0, D, 'power2_in'));

/** 盤のカードを借りるか（uid が分かるときだけ） */
const borrows = (payload) => Boolean(payload?.uid && payload?.fromZone);

export default defineEffect({
  situation: 'move_after_activation',
  role: 'motion',
  label: '発動後（透明になって消える）',
  color: '#b18cff',
  duration: () => D,
  sounds: () => [],
  // 消え終わりに盤面をイベントの後へ（それまでカードは魔法・罠ゾーンに居る）
  board: ({ payload }) => (borrows(payload) ? { at: D } : null),
  /* 盤のカードを借りる（R13）。元のゾーンの板だけ、素の濃さに alphaAt を掛ける */
  tweaks: ({ payload, localMs: t }) => {
    if (!borrows(payload) || t < 0 || t >= D) return {};
    const id = `card:${payload.fromZone}`;
    const a = alphaAt(t);
    return {
      [payload.uid]: (h) => {
        if (h.part?.id && h.part.id !== id) return;
        h.reset?.();
        (h.materials ?? []).forEach((m) => {
          m.opacity *= a;
          m.transparent = true;
        });
      },
    };
  },
  layers: [
    {
      id: 'fade_out',
      label: '発動後（借りられないときの板）',
      space: 'screen',
      script: (ctx) => {
        const { THREE } = ctx;
        const root = new THREE.Group();
        const { payload } = ctx;
        if (borrows(payload)) return { root };
        const box = zoneBox(ctx, payload?.fromZone);
        if (!box) return { root };
        const prev = payload.prev ?? {};
        const faceUp = prev.faceUp !== false;
        const c = card(ctx, { code: faceUp ? (prev.code ?? payload.code ?? null) : null, player: payload.player, faceUp, w: box.w });
        add(root, place(c.mesh, box.cx, box.cy), 0);
        return {
          root,
          update: ({ localMs: t }) => fade(c.mesh, alphaAt(t)),
        };
      },
    },
  ],
}, 'move_after_activation_motion.js');
